import React, { useEffect, useState, useRef } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete';
import { db, auth } from '../firebase';
import { getGoogleMapsApiKey } from '../services/googleMaps';

// Visual parity target: the "Shipping address" card on Profile.jsx.
// Field/validation parity target: same required fields + ZIP regex as Profile.
//
// This component does NOT replace Profile's address form. It is a focused,
// inline drop-in for the offer/accept flows so users don't have to leave the
// page to populate their shipping address before transacting.
//
// Writes both:
//   users/{uid}.firstName, .lastName              (root, used by shipping
//                                                  snapshots / email templates)
//   users/{uid}.shippingAddress                   ({ name, street1, street2,
//                                                   city, state, zip, country })
// `shippingAddress.name` is auto-derived from firstName + lastName so it stays
// consistent with what Profile.jsx writes.

const capitalizeWords = (str) => {
  if (!str) return '';
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
};

// Best-effort split of a single "First Last" string into a {first, last}
// pair. We split on the LAST whitespace so middle names/initials stay
// grouped with the first name (e.g. "Mary Jane Watson" -> first="Mary Jane",
// last="Watson"). Only used as a fallback prefill for legacy users whose
// user doc has shippingAddress.name but no root firstName/lastName.
const splitFullName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') return { first: '', last: '' };
  const trimmed = fullName.trim();
  if (!trimmed) return { first: '', last: '' };
  const idx = trimmed.lastIndexOf(' ');
  if (idx === -1) return { first: trimmed, last: '' };
  return {
    first: trimmed.slice(0, idx).trim(),
    last: trimmed.slice(idx + 1).trim(),
  };
};

const pickFirstName = (user) => {
  if (user?.firstName) return user.firstName;
  return splitFullName(user?.shippingAddress?.name).first;
};

const pickLastName = (user) => {
  if (user?.lastName) return user.lastName;
  return splitFullName(user?.shippingAddress?.name).last;
};

const inputStyle = {
  borderRadius: '25px',
  fontSize: '16px',
  borderWidth: '1px',
  fontWeight: 'normal',
  width: '100%',
  boxSizing: 'border-box',
  // Deliberately one step below the app's 11px 16px input standard: the modal
  // stacks six fields, and full-size inputs push it past the fold on phones.
  // (Jason's call, 2026-08-20.)
  padding: '9px 14px',
};

const labelStyle = {
  display: 'block',
  marginBottom: '0.35rem',
  fontWeight: '600',
  fontSize: '0.95rem',
  color: '#374151',
  fontFamily: 'Nunito, sans-serif',
};

// Tailwind is a dependency but was never wired into the build, so the
// `border-red-500` / `text-red-600` classes below render nothing: invalid fields
// looked identical to valid ones and the messages inherited the body grey.
// Anything that has to actually be red is an inline style. The red fill matters
// as much as the border — Safari draws its blue focus ring over the border of
// whichever field is focused, which is usually the one being complained about.
const errorTextStyle = {
  display: 'block',
  color: '#dc2626',
  fontSize: '0.8125rem',
  fontWeight: 600,
  marginTop: '0.25rem',
};

const invalidInputStyle = (hasError, extra) => ({
  ...inputStyle,
  ...extra,
  ...(hasError
    ? {
        borderColor: '#dc2626',
        borderStyle: 'solid',
        borderWidth: '2px',
        backgroundColor: '#fef2f2',
      }
    : null),
});

const AddressRequiredModal = ({
  isOpen,
  onClose,
  onSaved,
  user,
  context,
}) => {
  const initialAddress = user?.shippingAddress || {};
  const [firstName, setFirstName] = useState(pickFirstName(user));
  const [lastName, setLastName] = useState(pickLastName(user));
  const [street1, setStreet1] = useState(initialAddress.street1 || '');
  const [street2, setStreet2] = useState(initialAddress.street2 || '');
  const [city, setCity] = useState(initialAddress.city || '');
  const [state, setState] = useState(initialAddress.state || '');
  const [zip, setZip] = useState(initialAddress.zip || '');
  const [country, setCountry] = useState(initialAddress.country || 'US');

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  // Lazy-load Google Maps script on first focus of the street field
  // (matches Profile.jsx pattern: don't pull the API on every page load).
  const [placesApiLoaded, setPlacesApiLoaded] = useState(
    typeof window !== 'undefined' && !!(window.google && window.google.maps)
  );
  const [placesApiError, setPlacesApiError] = useState(false);
  const [isLoadingGoogleMaps, setIsLoadingGoogleMaps] = useState(false);
  const street1Ref = useRef(null);
  const pendingPlacesSwapRef = useRef(false);

  // Turning on placesApiLoaded swaps the plain street input for the
  // PlacesAutocomplete-wrapped one, which unmounts the field the user is in.
  // Refocusing afterwards can't fix that: iOS ignores a programmatic focus()
  // raised outside the original tap gesture, so the tap looks like it did
  // nothing and the user has to tap again. Hold the swap until they leave the
  // field instead. With the eager load below this rarely triggers at all.
  const activatePlacesInput = () => {
    setIsLoadingGoogleMaps(false);
    const street = street1Ref.current;
    if (street && document.activeElement === street) {
      pendingPlacesSwapRef.current = true;
      return;
    }
    setPlacesApiLoaded(true);
  };

  const applyPendingPlacesSwap = () => {
    if (!pendingPlacesSwapRef.current) return;
    pendingPlacesSwapRef.current = false;
    setPlacesApiLoaded(true);
  };

  const loadGoogleMapsScript = () => {
    if (window.google && window.google.maps) {
      activatePlacesInput();
      return;
    }
    if (isLoadingGoogleMaps) return;

    setIsLoadingGoogleMaps(true);
    const timeout = setTimeout(() => {
      if (!window.google || !window.google.maps) {
        setPlacesApiError(true);
        setIsLoadingGoogleMaps(false);
      }
    }, 10000);

    const existing = document.querySelector('script[data-pxc-google-maps]');
    if (existing) {
      // Another component already started loading the script; just wait for it.
      existing.addEventListener('load', () => {
        clearTimeout(timeout);
        activatePlacesInput();
      });
      existing.addEventListener('error', () => {
        clearTimeout(timeout);
        setPlacesApiError(true);
        setIsLoadingGoogleMaps(false);
      });
      return;
    }

    const script = document.createElement('script');
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${getGoogleMapsApiKey()}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.dataset.pxcGoogleMaps = '1';
    script.onload = () => {
      clearTimeout(timeout);
      activatePlacesInput();
    };
    script.onerror = () => {
      clearTimeout(timeout);
      setPlacesApiError(true);
      setIsLoadingGoogleMaps(false);
    };
    document.head.appendChild(script);
  };

  // Load Google Maps as soon as the modal opens rather than on first focus.
  // Loading on focus meant the input was always swapped mid-interaction, which
  // is what made the first tap on the street field do nothing. Entering an
  // address is this modal's entire purpose, so the fetch is never wasted.
  useEffect(() => {
    if (!isOpen) return;
    loadGoogleMapsScript();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on open only
  }, [isOpen]);

  // Re-prefill when the user prop hydrates after mount (e.g., firestoreUser
  // resolves slightly after the modal first renders). For legacy users who
  // have shippingAddress.name but no root firstName/lastName, fall back to
  // splitting the existing name string so they don't see empty Name fields.
  useEffect(() => {
    if (!isOpen) return;
    setFirstName((prev) => prev || pickFirstName(user));
    setLastName((prev) => prev || pickLastName(user));
    const a = user?.shippingAddress || {};
    setStreet1((prev) => prev || a.street1 || '');
    setStreet2((prev) => prev || a.street2 || '');
    setCity((prev) => prev || a.city || '');
    setState((prev) => prev || a.state || '');
    setZip((prev) => prev || a.zip || '');
    setCountry((prev) => prev || a.country || 'US');
  }, [isOpen, user]);

  // Lock body scroll while the modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const validate = () => {
    const e = {};
    if (!firstName.trim()) e.firstName = 'First name is required';
    if (!lastName.trim()) e.lastName = 'Last name is required';
    const street = street1.trim();
    if (!street) e.street1 = 'Street address is required';
    // Matches Profile.jsx: a lone number like "123" is what you're left with
    // after abandoning the autocomplete halfway, and it isn't shippable.
    else if (street.split(/\s+/).length < 2 || !/[a-zA-Z]/.test(street)) {
      e.street1 = 'Enter a street number and name, e.g. 123 Main St';
    }
    if (!city.trim()) e.city = 'City is required';
    if (!state.trim()) e.state = 'State is required';
    if (!zip.trim()) e.zip = 'ZIP code is required';
    else if (!/^\d{5}(-\d{4})?$/.test(zip.trim())) e.zip = 'Invalid ZIP code';
    if (!country.trim()) e.country = 'Country is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    setSaveError('');
    if (!validate()) return;
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setSaveError('You need to be signed in to save your address.');
      return;
    }

    setSaving(true);
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const fullName = `${trimmedFirst} ${trimmedLast}`.trim();
    const payload = {
      firstName: trimmedFirst,
      lastName: trimmedLast,
      shippingAddress: {
        name: fullName,
        street1: street1.trim(),
        street2: street2.trim(),
        city: city.trim(),
        state: state.trim(),
        zip: zip.trim(),
        country: country.trim() || 'US',
      },
    };

    try {
      await setDoc(doc(db, 'users', uid), payload, { merge: true });
      setSavedFlash(true);
      // Brief confirmation, then close.
      setTimeout(() => {
        setSavedFlash(false);
        if (onSaved) onSaved(payload);
      }, 700);
    } catch (err) {
      console.error('Failed to save shipping address:', err);
      setSaveError(
        'We could not save your address. Please try again, or update it from your Profile page.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && onClose) onClose();
  };

  const headerCopy = (() => {
    switch (context) {
      case 'trade-accept':
        return 'Add a shipping address to accept this offer';
      case 'purchase-accept':
        return 'Add a shipping address to accept this offer';
      case 'purchase-offer':
        return 'Add a shipping address to make a purchase offer';
      case 'shipping':
        // Seller/progress pages: the user is past the offer stage and the
        // address is needed for the shipping label, not for offering.
        return 'Add your shipping address to continue';
      case 'trade-offer':
      case 'direct-offer':
      case 'match-offer':
      default:
        return 'Add a shipping address to make an offer';
    }
  })();

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '2rem 1rem',
        overflowY: 'auto',
        fontFamily: 'Nunito, sans-serif',
      }}
    >
      <div
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          maxWidth: '480px',
          width: '100%',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          margin: 'auto',
        }}
      >
        <div
          style={{
            padding: '1rem 1.25rem',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          <h2
            style={{
              fontSize: '1.1rem',
              fontWeight: '700',
              margin: 0,
              color: '#374151',
              fontFamily: 'Nunito, sans-serif',
            }}
          >
            Shipping address
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                lineHeight: 1,
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '0 0.25rem',
              }}
            >
              ×
            </button>
          )}
        </div>

        <div style={{ padding: '1.25rem 1.25rem 1.5rem 1.25rem', textAlign: 'left' }}>
          <p
            style={{
              margin: '0 0 1.25rem 0',
              color: '#4b5563',
              fontSize: '0.95rem',
              lineHeight: '1.5',
              textAlign: 'center',
            }}
          >
            {headerCopy}. We use this to auto-fill shipping labels when a trade
            or purchase is accepted. You only need to do this once.
          </p>

          {/* First / Last name row */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>First name</label>
              <input
                className={`border focus:border-blue-500 focus:outline-none px-3 py-2 ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                }`}
                style={invalidInputStyle(errors.firstName)}
                value={firstName}
                onChange={(e) => setFirstName(capitalizeWords(e.target.value))}
                placeholder="First name"
                autoComplete="given-name"
                required
              />
              {errors.firstName && (
                <span
                  style={errorTextStyle}
                >
                  {errors.firstName}
                </span>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Last name</label>
              <input
                className={`border focus:border-blue-500 focus:outline-none px-3 py-2 ${
                  errors.lastName ? 'border-red-500' : 'border-gray-300'
                }`}
                style={invalidInputStyle(errors.lastName)}
                value={lastName}
                onChange={(e) => setLastName(capitalizeWords(e.target.value))}
                placeholder="Last name"
                autoComplete="family-name"
                required
              />
              {errors.lastName && (
                <span
                  style={errorTextStyle}
                >
                  {errors.lastName}
                </span>
              )}
            </div>
          </div>

          {/* Street Address */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Street address</label>
            {/* No "loading autocomplete" banner: it appeared above this field on
                the first tap and vanished a moment later, which read as a
                glitch. The plain input below is fully usable while the script
                loads, so there's nothing to wait on or act upon. */}
            {placesApiLoaded ? (
              <PlacesAutocomplete
                value={street1}
                onChange={(address) => setStreet1(address)}
                onSelect={async (address) => {
                  setStreet1(address);
                  try {
                    const results = await geocodeByAddress(address);
                    if (results && results[0]) {
                      const components = results[0].address_components;
                      const get = (types) => {
                        const c = components.find((cp) =>
                          types.every((t) => cp.types.includes(t))
                        );
                        return c ? c.long_name : '';
                      };
                      const stateShort = (() => {
                        const c = components.find((cp) =>
                          cp.types.includes('administrative_area_level_1')
                        );
                        return c ? c.short_name : '';
                      })();
                      setStreet1(
                        `${get(['street_number'])} ${get(['route'])}`.trim() ||
                          address
                      );
                      setCity(
                        get(['locality']) ||
                          get(['sublocality', 'political']) ||
                          city
                      );
                      setState(stateShort || state);
                      setZip(get(['postal_code']) || zip);
                      setCountry(get(['country']) || country || 'US');
                    }
                  } catch (err) {
                    console.error(
                      'Failed to parse address from Google Places:',
                      err
                    );
                  }
                }}
                searchOptions={{ componentRestrictions: { country: ['us'] } }}
                debounce={300}
                onError={(_status, clearSuggestions) => {
                  setPlacesApiError(true);
                  clearSuggestions();
                }}
              >
                {({
                  getInputProps,
                  suggestions,
                  getSuggestionItemProps,
                  loading,
                }) => (
                  <div style={{ position: 'relative' }}>
                    <input
                      {...getInputProps({
                        placeholder: 'Street address',
                        className: `border focus:border-blue-500 focus:outline-none px-3 py-2 ${
                          errors.street1 ? 'border-red-500' : 'border-gray-300'
                        }`,
                        style: invalidInputStyle(errors.street1),
                        onFocus: loadGoogleMapsScript,
                        autoComplete: 'address-line1',
                      })}
                      ref={street1Ref}
                      required
                    />
                    {(loading || suggestions.length > 0) && (
                      <div
                        className="autocomplete-dropdown-container bg-white border rounded shadow z-10"
                        style={{ marginTop: '4px' }}
                      >
                        {loading && (
                          <div style={{ padding: '6px 10px', color: '#6b7280' }}>
                            Loading...
                          </div>
                        )}
                        {suggestions.map((suggestion) => (
                          <div
                            {...getSuggestionItemProps(suggestion, {
                              className: suggestion.active
                                ? 'suggestion-item--active bg-blue-100 px-2 py-1 cursor-pointer'
                                : 'suggestion-item px-2 py-1 cursor-pointer',
                            })}
                            key={suggestion.placeId}
                          >
                            <span>{suggestion.description}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </PlacesAutocomplete>
            ) : (
              <input
                ref={street1Ref}
                className={`border focus:border-blue-500 focus:outline-none px-3 py-2 ${
                  errors.street1 ? 'border-red-500' : 'border-gray-300'
                }`}
                style={invalidInputStyle(errors.street1)}
                value={street1}
                onChange={(e) => setStreet1(e.target.value)}
                onFocus={loadGoogleMapsScript}
                onBlur={applyPendingPlacesSwap}
                placeholder="Street address"
                autoComplete="address-line1"
                required
              />
            )}
            {placesApiError && (
              <p className="text-sm text-red-500 mt-1 font-semibold">
                Address autocomplete failed to load. You can still type your
                address manually.
              </p>
            )}
            {errors.street1 && (
              <span
                style={errorTextStyle}
              >
                {errors.street1}
              </span>
            )}
          </div>

          {/* Street Address 2 */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Street address 2</label>
            <input
              className="border border-gray-300 focus:border-blue-500 focus:outline-none px-3 py-2"
              style={inputStyle}
              value={street2}
              onChange={(e) => setStreet2(e.target.value)}
              placeholder="Apt, Suite, etc."
              autoComplete="address-line2"
            />
          </div>

          {/* City and State row */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>City</label>
              <input
                className={`border focus:border-blue-500 focus:outline-none px-3 py-2 ${
                  errors.city ? 'border-red-500' : 'border-gray-300'
                }`}
                style={invalidInputStyle(errors.city)}
                value={city}
                onChange={(e) => setCity(capitalizeWords(e.target.value))}
                placeholder="City"
                autoComplete="address-level2"
                required
              />
              {errors.city && (
                <span
                  style={errorTextStyle}
                >
                  {errors.city}
                </span>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>State</label>
              <input
                className={`border focus:border-blue-500 focus:outline-none px-3 py-2 ${
                  errors.state ? 'border-red-500' : 'border-gray-300'
                }`}
                style={invalidInputStyle(errors.state)}
                value={state}
                onChange={(e) => setState(e.target.value.toUpperCase())}
                placeholder="State"
                maxLength={2}
                autoComplete="address-level1"
                required
              />
              {errors.state && (
                <span
                  style={errorTextStyle}
                >
                  {errors.state}
                </span>
              )}
            </div>
          </div>

          {/* ZIP and Country row */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>ZIP code</label>
              <input
                className={`border focus:border-blue-500 focus:outline-none px-3 py-2 ${
                  errors.zip ? 'border-red-500' : 'border-gray-300'
                }`}
                style={invalidInputStyle(errors.zip)}
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="ZIP code"
                autoComplete="postal-code"
                required
              />
              {errors.zip && (
                <span
                  style={errorTextStyle}
                >
                  {errors.zip}
                </span>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Country</label>
              <select
                className={`border focus:border-blue-500 focus:outline-none px-3 py-2 ${
                  errors.country ? 'border-red-500' : 'border-gray-300'
                }`}
                style={invalidInputStyle(errors.country, { backgroundColor: 'white' })}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
              >
                <option value="">Select country</option>
                <option value="US">United States</option>
              </select>
              {errors.country && (
                <span
                  style={errorTextStyle}
                >
                  {errors.country}
                </span>
              )}
            </div>
          </div>

          {saveError && (
            <p
              style={{
                color: '#dc2626',
                fontSize: '0.9rem',
                marginTop: '0.25rem',
                marginBottom: '1rem',
              }}
            >
              {saveError}
            </p>
          )}

          {/* Action row — matches Profile/Offer button conventions */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '12px',
              marginTop: '1.25rem',
            }}
          >
            <button
              type="button"
              className="hover:opacity-80 transition-opacity"
              onClick={handleSave}
              disabled={saving || savedFlash}
              style={{
                backgroundColor:
                  saving || savedFlash ? '#9CA3AF' : '#C9A227',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                padding: '10px 32px',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 'bold',
                fontSize: '16px',
                lineHeight: '1.2',
                cursor: saving || savedFlash ? 'not-allowed' : 'pointer',
              }}
            >
              {savedFlash
                ? 'Saved!'
                : saving
                ? 'Saving...'
                : 'Save Address'}
            </button>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                disabled={saving || savedFlash}
                style={{
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '10px 24px',
                  fontFamily: 'Nunito, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  lineHeight: '1.2',
                  cursor: saving || savedFlash ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!saving && !savedFlash) e.target.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => (e.target.style.opacity = '1')}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressRequiredModal;
